/**
 * Account API Client
 * ------------------
 * Manages parent accounts and children via the Lambda API.
 * Session/auth state stays in localStorage (via authService); this client
 * is responsible for persisting account *data* to MongoDB.
 *
 * API Gateway routes required (add to the same gateway as progressApi):
 *   POST   /accounts
 *   GET    /accounts/{uid}
 *   PATCH  /accounts/{uid}
 *   GET    /accounts/{uid}/children
 *   POST   /accounts/{uid}/children
 *   PATCH  /accounts/{uid}/children/{childId}
 *   DELETE /accounts/{uid}/children/{childId}
 */

import { firebaseAuth } from './firebase.js';

const API_BASE = import.meta.env.VITE_API_BASE;

async function apiFetch(path, options = {}) {
  const user = firebaseAuth.currentUser;
  const token = user ? await user.getIdToken() : null;
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const err = new Error(`Account API error: ${response.status}`);
    err.status = response.status;
    try { err.data = await response.json(); } catch (_) {}
    throw err;
  }

  return response.json();
}

export const accountApi = {
  /**
   * Create or upsert a parent account. Safe to call on every login.
   * On first call: generates a stable accountId, sets subscription defaults.
   * On repeat calls: updates email and name only.
   *
   * @param {{ uid: string, email: string, name: string, plan?: string }} params
   * @returns {Promise<{ account: object, created: boolean }>}
   */
  async createAccount({ uid, email, name, plan = 'free' }) {
    return apiFetch('/accounts', {
      method: 'POST',
      body: JSON.stringify({ uid, email, name, plan }),
    });
  },

  /**
   * Fetch full account details including children list (single round-trip).
   *
   * @param {string} uid
   * @returns {Promise<{ account: object }>}  account includes account.children[]
   */
  async getAccount(uid) {
    return apiFetch(`/accounts/${encodeURIComponent(uid)}`);
  },

  /**
   * Update mutable account fields.
   * Supported fields: name, onboardingComplete, subscription.plan, subscription.status
   *
   * @param {string} uid
   * @param {object} updates
   * @returns {Promise<{ success: boolean }>}
   */
  async updateAccount(uid, updates) {
    return apiFetch(`/accounts/${encodeURIComponent(uid)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  /**
   * List active children for a parent.
   *
   * @param {string} uid
   * @returns {Promise<{ children: object[], limit: { current, max, plan } }>}
   */
  async getChildren(uid) {
    return apiFetch(`/accounts/${encodeURIComponent(uid)}/children`);
  },

  /**
   * Add a child. Lambda enforces per-plan maxChildren limit.
   * Returns HTTP 403 with a descriptive message if the limit is reached.
   *
   * @param {string} uid
   * @param {string} name
   * @param {string|null} [childId] — optional: supply existing UUID when
   *   migrating children from localStorage so their progress data is preserved
   * @returns {Promise<{ child: object, created: boolean, limit: object }>}
   */
  async addChild(uid, name, childId = null) {
    return apiFetch(`/accounts/${encodeURIComponent(uid)}/children`, {
      method: 'POST',
      body: JSON.stringify({ name, ...(childId ? { childId } : {}) }),
    });
  },

  /**
   * Rename a child.
   *
   * @param {string} uid
   * @param {string} childId
   * @param {string} name
   * @returns {Promise<{ success: boolean, childId: string, name: string }>}
   */
  async updateChild(uid, childId, name) {
    return apiFetch(
      `/accounts/${encodeURIComponent(uid)}/children/${encodeURIComponent(childId)}`,
      { method: 'PATCH', body: JSON.stringify({ name }) },
    );
  },

  // ─── Subscription ───────────────────────────────────────────────────────

  /**
   * Upgrade to a higher plan. Lambda validates no downgrade is possible.
   * In mock phase this takes effect immediately; with Stripe it redirects to Checkout.
   *
   * @param {string} uid
   * @param {'pro'|'family'} newPlan
   */
  async upgradeSubscription(uid, newPlan) {
    return this.updateAccount(uid, { subscription: { plan: newPlan } });
  },

  /**
   * Cancel subscription at period end. Features stay active until renewsAt.
   * @param {string} uid
   */
  async cancelSubscription(uid) {
    return this.updateAccount(uid, { subscription: { status: 'cancelled' } });
  },

  /**
   * Reactivate a previously cancelled subscription before the period ends.
   * @param {string} uid
   */
  async reactivateSubscription(uid) {
    return this.updateAccount(uid, { subscription: { status: 'active' } });
  },

  // ─── Children ────────────────────────────────────────────────────────────

  /**
   * Schedule a downgrade to a lower plan (family → pro).
   * The plan change takes effect at the end of the current billing period (renewsAt).
   * Selected children are stamped with a scheduled archive date matching renewsAt.
   *
   * @param {string}   uid
   * @param {'pro'}    plan              Target plan (only 'pro' supported for now)
   * @param {string[]} childIdsToArchive Children to archive at the downgrade date
   * @returns {Promise<{ success: boolean }>}
   */
  async scheduleDowngrade(uid, plan, childIdsToArchive = []) {
    return this.updateAccount(uid, {
      scheduleDowngrade: { plan, childIdsToArchive },
    });
  },

  /**
   * Cancel a previously scheduled plan downgrade before it takes effect.
   * Restores scheduledArchiveAt children that were marked as part of the downgrade.
   *
   * @param {string} uid
   * @returns {Promise<{ success: boolean }>}
   */
  async cancelScheduledDowngrade(uid) {
    return this.updateAccount(uid, { cancelScheduledDowngrade: true });
  },

  /**
   * Schedule account closure at the end of the current billing period.
   * Cancels the Stripe subscription and marks the account for archival (not free plan revert).
   * @param {string} uid
   */
  async scheduleAccountClosure(uid) {
    return this.updateAccount(uid, { scheduleAccountClosure: true });
  },

  /**
   * Cancel a previously scheduled account closure before it takes effect.
   * Restores the subscription and clears the closure flag.
   * @param {string} uid
   */
  async cancelAccountClosure(uid) {
    return this.updateAccount(uid, { cancelAccountClosure: true });
  },

  /**
   * Immediately downgrade to the free plan.
   * Archives all existing children and resets onboardingComplete on the account.
   * Parent is expected to go through fresh onboarding after this call.
   *
   * @param {string} uid
   * @returns {Promise<{ success: boolean, archived: boolean }>}
   */
  async downgradeToFree(uid) {
    return this.updateAccount(uid, { downgradeToFree: true });
  },

  // ─── Stripe ──────────────────────────────────────────────────────────────

  /**
   * Create a Stripe Checkout Session for a new subscription.
   * Returns a { url } to redirect the user to the Stripe-hosted payment page.
   *
   * @param {{ uid: string, plan: 'pro'|'family', successUrl: string, cancelUrl: string }} params
   */
  async createCheckoutSession({ uid, plan, successUrl, cancelUrl }) {
    return apiFetch('/stripe/checkout', {
      method: 'POST',
      body: JSON.stringify({ uid, plan, success_url: successUrl, cancel_url: cancelUrl }),
    });
  },

  /**
   * Upgrade an existing Stripe subscription to a new plan in-place.
   * Stripe prorates the charge — the customer pays only the difference for
   * the remainder of the billing period, not the full new price.
   * Use this instead of createCheckoutSession when the user already has an
   * active subscription (avoids double-billing).
   *
   * @param {{ uid: string, plan: 'pro'|'family' }} params
   */
  async upgradeSubscription({ uid, plan }) {
    return apiFetch('/stripe/upgrade', {
      method: 'POST',
      body: JSON.stringify({ uid, plan }),
    });
  },

  /**
   * Create a Stripe Customer Portal session.
   * Returns a { url } to redirect the user to manage billing.
   *
   * @param {{ uid: string, returnUrl: string }} params
   */
  async createPortalSession({ uid, returnUrl }) {
    return apiFetch('/stripe/portal', {
      method: 'POST',
      body: JSON.stringify({ uid, return_url: returnUrl }),
    });
  },

  /**
   * Soft-delete a child. All progress data is preserved in MongoDB.
   *
   * @param {string} uid
   * @param {string} childId
   * @returns {Promise<{ success: boolean, childId: string, deleted: boolean }>}
   */
  async removeChild(uid, childId) {
    return apiFetch(
      `/accounts/${encodeURIComponent(uid)}/children/${encodeURIComponent(childId)}`,
      { method: 'DELETE' },
    );
  },
};

export default accountApi;
